import { useState } from "react"
import Link from "next/link"
import gql from "graphql-tag"
import { useQuery } from "react-apollo"

import HeaderSignIn from "../header/header-sign-in"

import { needsLine } from "../../data/skill-phrases"

import "./featured-ideas.scss"

// Picked by the backend (utils/featured-ideas.js): recent, written as an idea
// rather than a recruiting post, one per founder.
export const FEATURED_IDEAS_QUERY = gql`
  query FEATURED_IDEAS($first: Int) {
    featuredIdeas(first: $first) {
      id
      title
      elevatorPitch
      ideaStage {
        itemId
        label
      }
      createdBy {
        id
        locationCountry
        wantsSkills {
          itemId
        }
      }
    }
  }
`

const plain = (s) =>
  String(s || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/[*_#>`~]/g, "")
    .replace(/\s+/g, " ")
    .trim()

// "Validation - Concept exploration & research" → "Validation"
export const shortStage = (stage) => (stage && stage.label ? stage.label.split(" - ")[0].trim() : null)

// The grid runs 4 columns, then 2, then 1, so the card count is a multiple
// of 4 (or 2 when there are few ideas); the last card is a blurred idea that
// asks visitors to sign in.
const cardCount = (n) => (n >= 4 ? n - (n % 4) : n >= 2 ? 2 : n)

const IdeaCard = ({ idea }) => {
  const meta = [shortStage(idea.ideaStage), idea.createdBy && idea.createdBy.locationCountry]
    .filter(Boolean)
    .join(" · ")
  const needs = needsLine(idea.createdBy && idea.createdBy.wantsSkills)
  return (
    <article className="featured-ideas__card">
      {meta && <span className="featured-ideas__meta">{meta}</span>}
      <h3 className="featured-ideas__name">
        <Link href="/ideas/[id]" as={`/ideas/${idea.id}`}>
          <a>{idea.title}</a>
        </Link>
      </h3>
      <p className="featured-ideas__pitch">{plain(idea.elevatorPitch)}</p>
      {needs && <span className="featured-ideas__needs">{needs}</span>}
    </article>
  )
}

// The veil is blank lines, not a real idea: blurred text is still in the page.
const LockedCard = () => {
  const [signInOpen, setSignInOpen] = useState(false)
  return (
    <article className="featured-ideas__card featured-ideas__card--locked">
      <div className="featured-ideas__veil" aria-hidden="true">
        <span className="featured-ideas__skeleton featured-ideas__skeleton--meta" />
        <span className="featured-ideas__skeleton featured-ideas__skeleton--name" />
        {[100, 92, 100, 64].map((width, i) => (
          <span className="featured-ideas__skeleton" style={{ width: `${width}%` }} key={i} />
        ))}
      </div>
      <div className="featured-ideas__lock">
        <p className="featured-ideas__lock-copy">Sign in to see more ideas</p>
        <button type="button" className="featured-ideas__lock-btn" onClick={() => setSignInOpen(!signInOpen)}>
          Sign in
        </button>
        <Link href="/welcome">
          <a className="featured-ideas__lock-join">or join free</a>
        </Link>
      </div>
      {signInOpen && (
        <div className="featured-ideas__signin">
          <HeaderSignIn toggleSignIn={() => setSignInOpen(false)} />
        </div>
      )}
    </article>
  )
}

const FeaturedIdeas = ({ first = 8, title = "Ideas looking for co-founders" }) => {
  const { data } = useQuery(FEATURED_IDEAS_QUERY, { variables: { first } })
  const ideas = ((data && data.featuredIdeas) || []).slice(0, cardCount(first))
  const shown = ideas.slice(0, cardCount(ideas.length))
  if (shown.length === 0) return null
  const locked = shown.length >= 2 ? shown[shown.length - 1] : null
  const open = locked ? shown.slice(0, -1) : shown

  return (
    <section className="featured-ideas" aria-labelledby="featured-ideas-title">
      <div className="featured-ideas__head">
        <h2 id="featured-ideas-title" className="featured-ideas__title">
          {title}
        </h2>
        <Link href="/ideas">
          <a className="featured-ideas__all">See all ideas</a>
        </Link>
      </div>
      <div className="featured-ideas__grid">
        {open.map((idea) => (
          <IdeaCard key={idea.id} idea={idea} />
        ))}
        {locked && <LockedCard />}
      </div>
    </section>
  )
}

export default FeaturedIdeas
